// import { getNumberOfCocktails } from './getCocktailProducts';
// import { getPlatformServiceFee } from './getPlatformFees';

import { CustomSupply, Package, PackageAddon, PackageAddonType, PackageType } from "../../types/package";

/**
 * Get the number of bartenders needed for a number of guests
 * @param guests Number of guests
 * @param guestsPerBartender Configured number of guests per bartender in package
 */

const PLATFORM_SERVICE_FEE_BP = 750;
export function getPlatformServiceFee(amount: number) {
  return Math.ceil((amount * PLATFORM_SERVICE_FEE_BP) / 10000);
}

export function getNumberOfCocktails(guests: number, numberOfHours: number) {
  return guests * (numberOfHours + 1);
}
export function getNumberOfBartenders(
  guests: number,
  guestsPerBartender: number
) {
  return Math.ceil(guests / guestsPerBartender);
}

/**
 * Get the total package cost for a booking
 * @param pkg Package record
 * @param guests Number of guests
 * @param numberOfHours Length of the event in hours
 */
export function getPackageTotal(
  bartenderPriceTotal: number,
  deliveryTotal: number,
  tipsTotal: number,
  additionalProfit: number,
  marketingExpense: number,
  insuranceExpense: number,
  type: PackageType,
  minPrice: number,
  customSupplyTotal: number,
) {
  if (type === PackageType.CustomPackage) {
    return Math.max(
      bartenderPriceTotal +
      deliveryTotal +
      tipsTotal +
      additionalProfit +
      marketingExpense +
      insuranceExpense +
      customSupplyTotal,
      minPrice,
    );
  } else {
    return Math.max(
      bartenderPriceTotal +
      deliveryTotal +
      tipsTotal +
      additionalProfit +
      marketingExpense +
      insuranceExpense,
      minPrice,
    );
  }
}

export function getPackageTotalCost(
  pkg: Pick<
    Package,
    'bartenderCost' | 'guestsPerBartender' | 'prepMins' | 'suppliesCost' | 'guestsPerBeer' | 'type'
  >,
  guests: number,
  numberOfHours: number,
  isCocktail: boolean,
): number {
  const guestsPerBartender =
    pkg.type == PackageType.CustomPackage
      ? isCocktail
        ? pkg.guestsPerBartender
        : pkg.guestsPerBeer
      : pkg.guestsPerBartender;
  return (
    getNumberOfBartenders(guests, guestsPerBartender) *
      pkg.bartenderCost *
      (numberOfHours + pkg.prepMins / 60) +
    getNumberOfCocktails(guests, numberOfHours) * pkg.suppliesCost
  );
}
export function getBartenderPriceTotal(
  pkg: Pick<
    Package,
    | 'type'
    | 'bartenderPrice'
    | 'guestsPerBartender'
    | 'prepMins'
    | 'suppliesPrice'
    | 'marketingExpense'
    | 'additionalProfit'
    | 'insuranceExpense'
    | 'isTipPercentage'
    | 'guestsPerBeer'
  >,
  guests: number,
  numberOfHours: number,
  guestsPerBartender: number,
): number {
  let TotalPrice =
    getNumberOfBartenders(guests, guestsPerBartender) *
    pkg.bartenderPrice *
    (numberOfHours + pkg.prepMins / 60);
  if (pkg.type != PackageType.CustomPackage) {
    TotalPrice = TotalPrice + getNumberOfCocktails(guests, numberOfHours) * pkg.suppliesPrice;
  }
  return TotalPrice;
}
/**
 * Get the total package cost for a booking
 * @param pkg Package record
 * @param distance Distance to the event in miles
 */
export function getDeliveryTotal(
  pkg: Pick<Package, "deliveryFee" | "mileageFee">,
  distance: number
): number {
  return (
    pkg.deliveryFee +
    Math.ceil(
      (pkg.mileageFee * Math.ceil(Math.max(distance - 30, 0)) * 2) / 100
    ) *
    100
  );
}

/**
 * Get the total tips cost for a booking (if tips are optional)
 * @param pkg Package record
 * @param guests Number of guests
 * @param tips Whether tips will be collected at the event
 */
export function getTipsTotal(
  pkg: Pick<
    Package,
    | "tipsOptional"
    | "tipsFee"
    | "isTipPercentage"
    | "bartenderPrice"
    | "prepMins"
  >,
  guests: number,
  tips: boolean,
  numberOfBartenders: number,
  hrToRegister: number
): number {
  // return pkg.tipsOptional && !tips ? (pkg.tipsFee || 0) * guests : 0;
  if (pkg.tipsOptional) {
    if (pkg.isTipPercentage && !tips) {
      return (
        ((pkg.tipsFee || 0) / 100) *
        (Number(numberOfBartenders) *
          Number(pkg.bartenderPrice) *
          (Number(hrToRegister) + Number(pkg.prepMins) / 60))
      );
    } else {
      return !tips ? (pkg.tipsFee || 0) * guests : 0;
    }
  } else {
    return 0;
  }
}
/**
 * Get the total cost of a specific package addon based on its price type
 * @param addon PackageAddon record
 * @param guests Number of guests
 * @param bartenders Number of bartenders
 */
export function getAddonPrice(
  addon: Pick<PackageAddon, 'price' | 'type' | 'include_prep_time' | 'min_price'>,
  guests: number,
  bartenders: number,
  numberOfHours: number,
  prepMins: number,
): number {
  console.log('========addons=========');
  console.log(addon);
  console.log('guests', guests);
  console.log('bartenders', bartenders);
  console.log('numberOfHours', numberOfHours);
  console.log('prepMins', prepMins);
  console.log('========addons=========');
  switch (addon.type) {
    case PackageAddonType.Bartender:
      return Math.max(addon.price * bartenders, addon.min_price);
    case PackageAddonType.Guests:
      return Math.max(addon.price * guests, addon.min_price);
    case PackageAddonType.Hourly:
      if (addon.include_prep_time) {
        return Math.max(addon.price * (numberOfHours + prepMins / 60), addon.min_price);
      } else {
        return Math.max(addon.price * numberOfHours, addon.min_price);
      }
    case PackageAddonType.NumOfCocktails:
      return Math.max(addon.price * getNumberOfCocktails(guests, numberOfHours), addon.min_price);
    case PackageAddonType.Fixed:
    default:
      return Math.max(addon.price, addon.min_price);
  }
}

/**
 * Get the total package addons cost for a booking
 * @param addons PackageAddon records
 * @param guests Number of guests
 * @param bartenders Number of bartenders
 */
export function getAddonsTotal(
  addons: Pick<PackageAddon, "price" | "type" | "include_prep_time" | "min_price"|"auto_include">[],
  guests: number,
  bartenders: number,
  numberOfHours: number,
  prepMins: number
): number {
  return addons.reduce((acc, addon) => {
    if (addon.auto_include) {
      acc += getAddonPrice(addon, guests, bartenders, numberOfHours,prepMins);
      return acc;
    } else {
      return acc;
    }
  }, 0);
}
export function getAddonCost(
  addon: Pick<PackageAddon, "cost" | "type">,
  guests: number,
  bartenders: number,
  numberOfHours: number
): number {
  switch (addon.type) {
    case PackageAddonType.Bartender:
      return addon.cost * bartenders;
    case PackageAddonType.Guests:
      return addon.cost * guests;
    case PackageAddonType.Hourly:
      return addon.cost * numberOfHours;
    case PackageAddonType.NumOfCocktails:
      return addon.cost * getNumberOfCocktails(guests, numberOfHours);
    case PackageAddonType.Fixed:
    default:
      return addon.cost;
  }
}
export function getAddonsCostTotal(
  addons: Pick<PackageAddon, "cost" | "type" | "auto_include">[],
  guests: number,
  bartenders: number,
  numberOfHours: number
): number {
  return addons.reduce((acc, addon) => {
    if (addon.auto_include) {
      acc += getAddonCost(addon, guests, bartenders, numberOfHours);
      return acc;
    } else {
      return acc;
    }
  }, 0);
}
export function getCustomSupply(
  supply: CustomSupply,
  guests: number,
  bartenders: number,
  numberOfHours: number,
) {
  switch (supply.type) {
    case PackageAddonType.Bartender:
      return supply.price * bartenders;
    case PackageAddonType.Guests:
      return supply.price * guests;
    case PackageAddonType.Hourly:
      return supply.price * numberOfHours;
    case PackageAddonType.NumOfCocktails:
      return supply.price * getNumberOfCocktails(guests, numberOfHours);
    case PackageAddonType.Fixed:
    default:
      return supply.price;
  }
}
export function getCustomSupplyTotal(
  customSupply: CustomSupply[],
  guests: number,
  bartenders: number,
  numberOfHours: number,
): number {
  return customSupply.reduce((acc, supply) => {
    acc += getCustomSupply(supply, guests, bartenders, numberOfHours);
    return acc;
  }, 0);
}

/**
 * Get total service cost and cost breakdown for a booking
 * @param pkg Package record
 * @param addons PackageAddon records
 * @param guests Number of guests
 * @param numberOfHours Length of the event in hours
 * @param distance The distance in miles to the event
 * @param tips Whether tips will be collected at the event
 */
export function getServiceTotal(
  pkg: Pick<
    Package,
    | 'type'
    | "bartenderPrice"
    | "guestsPerBartender"
    | "prepMins"
    | "suppliesPrice"
    | "deliveryFee"
    | "mileageFee"
    | "tipsOptional"
    | "tipsFee"
    | "bartenderCost"
    | "suppliesCost"
    | "minPrice"
    | "minHours"
    | "additionalProfit"
    | "insuranceExpense"
    | "marketingExpense"
    | "isTipPercentage"
    | 'guestsPerBeer'
    | 'withPrep'
    | 'minTips'
  >,
  addons: Pick<PackageAddon, 'price' | 'type' | 'cost' | 'include_prep_time' | 'min_price'| "auto_include">[],
  guests: number,
  numberOfHours: number,
  distance: number,
  tips: boolean,
  customSupply: CustomSupply[] = [],
  isCocktail: boolean = false,
  isBeer: boolean = false,
  isWine: boolean = false,
) {
  console.log('=======================arguments=======================');
  console.log('pkg', pkg);
  console.log('addons', addons);
  console.log('guests', guests);
  console.log('numberOfHours', numberOfHours);
  console.log('distance', distance);
  console.log('tips', tips);
  console.log('customSupply', customSupply);
  console.log('isCocktail', isCocktail);
  console.log('isBeer', isBeer);
  console.log('isWine', isWine);
  console.log('=======================arguments=======================');
  const guestsPerBartenderCustom =
    isCocktail == false && isBeer == false && isWine == false
      ? pkg.guestsPerBartender
      : isCocktail == true
        ? pkg.guestsPerBartender
        : pkg.guestsPerBeer;

  const guestsPerBartender =
    pkg.type == PackageType.CustomPackage ? guestsPerBartenderCustom : pkg.guestsPerBartender;
  numberOfHours = Number(numberOfHours);
  console.log('guestsPerBartender', guestsPerBartender);
  const deliveryTotal = getDeliveryTotal(pkg, distance);
  console.log('deliveryTotal', deliveryTotal);
  const tipsTotal = Math.max(
    getTipsTotal(
      pkg,
      guests,
      tips,
      getNumberOfBartenders(guests, guestsPerBartender),
      numberOfHours,
    ),
    0,
  );
  console.log('tipsTotal', tipsTotal);
  const numberofhours: number = numberOfHours;
  console.log('numberofhours', numberofhours);
  const addonsTotal = getAddonsTotal(
    addons,
    guests,
    getNumberOfBartenders(guests, guestsPerBartender),
    numberofhours,
    pkg.prepMins,
  );
  console.log('addonsTotal', addonsTotal);
  const addonsTotalCost = getAddonsCostTotal(
    addons,
    guests,
    getNumberOfBartenders(guests, guestsPerBartender),
    numberOfHours
  );
  console.log('addonsTotalCost', addonsTotalCost);
  const customSupplyTotal = getCustomSupplyTotal(
    customSupply.filter((cs) => cs.isBtcProvide),
    guests,
    getNumberOfBartenders(guests, guestsPerBartender),
    numberOfHours,
  );
  console.log('customSupplyTotal', customSupplyTotal);
  const bartenderPriceTotal = getBartenderPriceTotal(
    pkg,
    guests,
    numberOfHours,
    guestsPerBartender,
  );
  console.log('bartenderPriceTotal', bartenderPriceTotal);
  const packageTotal = getPackageTotal(
    bartenderPriceTotal,
    deliveryTotal,
    tipsTotal,
    pkg.additionalProfit,
    pkg.marketingExpense,
    pkg.insuranceExpense,
    pkg.type as PackageType,
    pkg.minPrice as number,
    customSupplyTotal,
  );
  console.log('packageTotal', packageTotal);
  const packageTotalCost =
    getPackageTotalCost(pkg, guests, numberOfHours, !!isCocktail) +
    pkg.additionalProfit +
    pkg.marketingExpense +
    pkg.insuranceExpense;
    const serviceSubtotalCost = packageTotalCost + addonsTotalCost;
    const serviceSubtotal = packageTotal + addonsTotal;
    const serviceFees = getPlatformServiceFee(serviceSubtotal);
    console.log('serviceFees', serviceFees);
    console.log('serviceSubtotal', serviceSubtotal);
    console.log('serviceSubtotalCost', serviceSubtotalCost);
  console.log('serviceTotal', serviceSubtotal + serviceFees);
  console.log('serviceTotalCost', serviceSubtotalCost + serviceFees);
  console.log('=======================completed=======================');
  return {
    packageTotal: packageTotal - tipsTotal,
    deliveryTotal,
    tipsTotal,
    addonsTotal,
    serviceFees,
    serviceTotal: serviceSubtotal + serviceFees,
    serviceTotalCost: serviceSubtotalCost + serviceFees,
  };
}
